import { useEffect, useRef, useState } from "react";
import { readConfig } from "../../../lib/config";
import { acceptLive } from "../lib/live";
import type { MbLive } from "../lib/live";

/** 시험이 바꿔 끼우는 창(메시지 듣기) — 기본은 window */
export interface LiveWindow {
  addEventListener(type: "message", h: (ev: MessageEvent) => void): void;
  removeEventListener(type: "message", h: (ev: MessageEvent) => void): void;
}

/**
 * Bench 편집기 탭이 넘기는 보드 모습을 듣는다(lib/live · 설정된 Bench 출처에서만). 마지막 모습과 받은 시각(ms)을 준다 —
 * 2초 넘게 소식이 없으면 화면이 '연결 끊김'을 보인다(Bench 탭을 닫았거나 시뮬레이터를 멈췄다).
 * `onFrame` 은 소식마다 받은 그 자리에서 부른다 — 흐르는 글자 · 출발 시각처럼 한 소식도 놓치면 안 되는 놀이(lib/lessons frameLessons).
 * 화면이 다시 그려지기 전에 소식 둘이 와도 둘 다 넘긴다(마지막 모습만 남는 live 와 다르다).
 */
/**
 * `accept` — 받을 소식을 더 거른다(놀이터 왼쪽 칸이 있으면 **그 칸에서 온 것만** — 'Bench 에서 고치기' 탭의 시뮬레이터가 섞이지 않게).
 */
export function useMicrobitLive(win: LiveWindow = window, benchOrigin: string | null = readConfig().benchOrigin, onFrame?: (v: MbLive, at: number) => void,
  accept?: (ev: MessageEvent) => boolean) {
  const [live, setLive] = useState<MbLive | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;
  const acceptRef = useRef(accept);
  acceptRef.current = accept;
  useEffect(() => {
    if (!benchOrigin) return;
    const h = (ev: MessageEvent) => {
      const v = acceptLive(ev, benchOrigin);
      if (!v || (acceptRef.current && !acceptRef.current(ev))) return;
      const now = Date.now();
      setLive(v);
      setAt(now);
      onFrameRef.current?.(v, now);
    };
    win.addEventListener("message", h);
    return () => win.removeEventListener("message", h);
  }, [win, benchOrigin]);
  return { live, at, available: !!benchOrigin };
}
